import {
  applyPremiumMultiplier,
  calculateParticipationPoints,
  calculateRecordPoints,
  calculateTop8Points,
  getLeagueScoring,
} from "./scoring";
import { getCompetitionRanks } from "./commander";

export interface LeaguePlayerSummary {
  playerConstructId: string;
  totalPoints: number;
  totalEvents: number;
  totalWins: number;
  totalLosses: number;
  totalDraws: number;
  bestRank: number;
  averageRank: number;
  averageFinishPercentile: number;
  currentRank: number;
}

export function calculateFinishPercentile(
  rank: number,
  fieldSize: number
): number {
  if (fieldSize <= 1) return 100;
  return ((fieldSize - rank + 1) / fieldSize) * 100;
}

export function calculateWinRate(
  wins: number,
  losses: number,
  draws: number
): number {
  const matches = wins + losses + draws;
  return matches > 0 ? (wins / matches) * 100 : 0;
}

export function compareLeagueStandings(
  a: Pick<
    LeaguePlayerSummary,
    "totalPoints" | "averageFinishPercentile" | "totalWins" | "averageRank"
  >,
  b: Pick<
    LeaguePlayerSummary,
    "totalPoints" | "averageFinishPercentile" | "totalWins" | "averageRank"
  >
): number {
  return (
    b.totalPoints - a.totalPoints ||
    b.averageFinishPercentile - a.averageFinishPercentile ||
    b.totalWins - a.totalWins ||
    a.averageRank - b.averageRank
  );
}

export function aggregateLeaguePlayerStats(
  league: any,
  allEvents: any[],
  allResults: any[],
  allTopCutResults: any[],
  allAdjustments: any[] = []
): LeaguePlayerSummary[] {
  const scoring = getLeagueScoring(league);
  const events = allEvents.filter((event) => event.leagueId === league.id);
  const eventIds = new Set(events.map((event) => event.id));
  const results = allResults.filter((result) => eventIds.has(result.eventId));
  const summaries = new Map<
    string,
    Omit<
      LeaguePlayerSummary,
      "averageRank" | "averageFinishPercentile" | "currentRank"
    > & { rankTotal: number; percentileTotal: number }
  >();

  for (const result of results) {
    const event = events.find((item) => item.id === result.eventId);
    const fieldSize =
      event?.fieldSize ||
      results.filter((item) => item.eventId === result.eventId).length;
    const existing = summaries.get(result.playerConstructId) || {
      playerConstructId: result.playerConstructId,
      totalPoints: 0,
      totalEvents: 0,
      totalWins: 0,
      totalLosses: 0,
      totalDraws: 0,
      bestRank: Number.POSITIVE_INFINITY,
      rankTotal: 0,
      percentileTotal: 0,
    };
    const isCommander = event?.format === "Commander";
    const matchPoints = isCommander
      ? result.points || 0
      : calculateRecordPoints(
          result.wins || 0,
          result.draws || 0,
          result.losses || 0,
          scoring
        );
    const placementPoints = isCommander
      ? 0
      : allTopCutResults
          .filter(
            (topCut) =>
              topCut.eventId === result.eventId &&
              topCut.playerConstructId === result.playerConstructId
          )
          .reduce(
            (sum, topCut) => sum + calculateTop8Points(topCut.rank, scoring),
            0
          );

    existing.totalPoints +=
      applyPremiumMultiplier(
        matchPoints + placementPoints,
        event?.isPremium === true,
        scoring
      ) + calculateParticipationPoints(1, scoring);
    existing.totalEvents += 1;
    existing.totalWins += result.wins || 0;
    existing.totalLosses += result.losses || 0;
    existing.totalDraws += result.draws || 0;
    existing.bestRank = Math.min(existing.bestRank, result.rank);
    existing.rankTotal += result.rank;
    existing.percentileTotal += calculateFinishPercentile(
      result.rank,
      fieldSize
    );
    summaries.set(result.playerConstructId, existing);
  }

  allAdjustments
    .filter(
      (adjustment) =>
        adjustment.leagueId === league.id && adjustment.playerConstructId
    )
    .forEach((adjustment) => {
      const existing = summaries.get(adjustment.playerConstructId) || {
        playerConstructId: adjustment.playerConstructId,
        totalPoints: 0,
        totalEvents: 0,
        totalWins: 0,
        totalLosses: 0,
        totalDraws: 0,
        bestRank: Number.POSITIVE_INFINITY,
        rankTotal: 0,
        percentileTotal: 0,
      };
      existing.totalPoints += adjustment.points;
      summaries.set(adjustment.playerConstructId, existing);
    });

  const ranked = Array.from(summaries.values())
    .map((summary) => ({
      playerConstructId: summary.playerConstructId,
      totalPoints: summary.totalPoints,
      totalEvents: summary.totalEvents,
      totalWins: summary.totalWins,
      totalLosses: summary.totalLosses,
      totalDraws: summary.totalDraws,
      bestRank: summary.bestRank,
      averageRank:
        summary.totalEvents > 0 ? summary.rankTotal / summary.totalEvents : 0,
      averageFinishPercentile:
        summary.totalEvents > 0
          ? summary.percentileTotal / summary.totalEvents
          : 0,
      currentRank: 0,
    }));

  if (league.format === "Commander") {
    return getCompetitionRanks(
      ranked.sort((a, b) => b.totalPoints - a.totalPoints),
      (summary) => summary.totalPoints
    ).map(({ rank, ...summary }) => ({ ...summary, currentRank: rank }));
  }
  return ranked
    .sort(compareLeagueStandings)
    .map((summary, index) => ({ ...summary, currentRank: index + 1 }));
}
